import { useMemo, useState } from "react";
import { Button, Input, Modal, Select } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";

import { useInvestigatorDirectory } from "@/hooks/usePlannedSites";
import type { InvestigatorProfile } from "./InvestigatorProfileDrawer";
import { currentSite } from "../plannedSites";

const EXPERIENCE_OPTIONS = [
  { value: 0, label: "Any experience" },
  { value: 5, label: "5+ years" },
  { value: 10, label: "10+ years" },
  { value: 20, label: "20+ years" },
];

interface AddInvestigatorModalProps {
  open: boolean;
  onClose: () => void;
  /** Investigators already on the plan (by inv_key) — hidden from the picker. */
  existingKeys: Set<string>;
  onAdd: (inv: InvestigatorProfile) => void;
}

/**
 * Manual additions to the applied shortlist. Filter parameters narrow the
 * directory before searching, so a manual pick can start from "thoracic
 * oncologists in Europe with 10+ years" rather than a name.
 */
export default function AddInvestigatorModal({
  open,
  onClose,
  existingKeys,
  onAdd,
}: AddInvestigatorModalProps) {
  const { investigators, isLoading } = useInvestigatorDirectory();

  const [search, setSearch] = useState("");
  const [ta, setTa] = useState<string | undefined>();
  const [specialty, setSpecialty] = useState<string | undefined>();
  const [region, setRegion] = useState<string | undefined>();
  const [minYears, setMinYears] = useState(0);

  const allTas = useMemo(
    () =>
      [
        ...new Set(investigators.flatMap((i) => i.therapeutic_areas ?? [])),
      ].sort(),
    [investigators],
  );
  const allSpecialties = useMemo(
    () =>
      [
        ...new Set(investigators.map((i) => i.specialty).filter(Boolean)),
      ].sort(),
    [investigators],
  );
  const allRegions = useMemo(
    () =>
      [
        ...new Set(investigators.map((i) => i.region ?? "").filter(Boolean)),
      ].sort(),
    [investigators],
  );

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return investigators
      .filter((inv) => !existingKeys.has(inv.inv_key))
      .filter((inv) => !ta || (inv.therapeutic_areas ?? []).includes(ta))
      .filter((inv) => !specialty || inv.specialty === specialty)
      .filter((inv) => !region || inv.region === region)
      .filter((inv) => (inv.years_experience ?? 0) >= minYears)
      .filter(
        (inv) =>
          !q ||
          inv.name.toLowerCase().includes(q) ||
          (currentSite(inv)?.name ?? "").toLowerCase().includes(q),
      )
      .slice(0, 25);
  }, [investigators, existingKeys, search, ta, specialty, region, minYears]);

  return (
    <Modal
      title="Add Investigator"
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      destroyOnHidden
    >
      <p className="text-[12.5px] text-[#6B7280] mt-1">
        Search the directory. Manual additions are marked so they stay
        distinguishable from the algorithm's list.
      </p>
      <Input
        prefix={<SearchOutlined className="text-[#9CA3AF]" />}
        placeholder="PI or site name…"
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="flex gap-2 mt-2 flex-wrap">
        <Select
          size="small"
          className="flex-1 min-w-[200px]"
          placeholder="Therapeutic area"
          allowClear
          value={ta}
          options={allTas.map((t) => ({ value: t, label: t }))}
          onChange={setTa}
        />
        <Select
          size="small"
          className="flex-1 min-w-[200px]"
          placeholder="Specialty"
          allowClear
          value={specialty}
          options={allSpecialties.map((s) => ({ value: s, label: s }))}
          onChange={setSpecialty}
        />
        <Select
          size="small"
          className="flex-1 min-w-[160px]"
          placeholder="Region"
          allowClear
          value={region}
          options={allRegions.map((r) => ({ value: r, label: r }))}
          onChange={setRegion}
        />
        <Select
          size="small"
          className="w-[150px]"
          value={minYears}
          options={EXPERIENCE_OPTIONS}
          onChange={setMinYears}
        />
      </div>
      <div className="max-h-[320px] overflow-y-auto mt-3 -mx-1 px-1">
        {isLoading ? (
          <p className="text-[12px] text-[#8A97A3] py-3">Loading directory…</p>
        ) : matches.length === 0 ? (
          <p className="text-[12px] text-[#8A97A3] py-3">
            No matches — adjust the filters, or everyone matching is already on
            this plan.
          </p>
        ) : (
          matches.map((inv) => {
            const site = currentSite(inv);
            return (
              <div
                key={inv.inv_key}
                className="flex items-center gap-3 py-2 border-b border-[#F5F7F8] last:border-b-0"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[13px] text-[#1F2933] m-0 leading-snug">
                    {inv.name}
                  </p>
                  <p className="text-[11.5px] text-[#6B7280] m-0 truncate">
                    {[
                      site?.name,
                      inv.specialty,
                      inv.years_experience
                        ? `${inv.years_experience}y`
                        : undefined,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <Button
                  size="small"
                  type="primary"
                  ghost
                  icon={<PlusOutlined />}
                  onClick={() => onAdd(inv)}
                >
                  Add
                </Button>
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
}
